'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { Toaster } from '@/components/ui/sonner';
import { useEffect, useState } from 'react';
import { msalConfig } from '@/lib/msal-config';
import { useLanguageStore } from '@/lib/i18n/language-store';

function LanguageHtmlSync() {
  const language = useLanguageStore((state) => state.language);

  useEffect(() => {
    document.documentElement.lang = language;

    document.title =
      language === 'en'
        ? 'Procurement Agent | Schaufler Fischer Group'
        : 'Beschaffungsagent | Schaufler Fischer Group';

    const descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        language === 'en'
          ? 'AI-supported delivery tracking and shipping handling'
          : 'KI-gestützte Lieferterminverfolgung & Versandabwicklung'
      );
    }
  }, [language]);

  return null;
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [msalInstance] = useState(() => {
    try {
      return new PublicClientApplication(msalConfig);
    } catch (error) {
      console.error('[MSAL] Initialisierung fehlgeschlagen, verwende sicheren Fallback.', error);
      return new PublicClientApplication({
        auth: {
          clientId: '00000000-0000-0000-0000-000000000000',
          authority: 'https://login.microsoftonline.com/common',
          redirectUri: typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000',
          postLogoutRedirectUri:
            typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000',
        },
      });
    }
  });

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5_000,
            retry: 1,
            refetchOnWindowFocus: true,
          },
        },
      })
  );

  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
          <LanguageHtmlSync />
          {children}
          <Toaster richColors position="bottom-right" />
        </ThemeProvider>
      </QueryClientProvider>
    </MsalProvider>
  );
}
